import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

import { SIDEBAR_DEFAULT_WIDTH, SIDEBAR_MAX_WIDTH, SIDEBAR_MIN_WIDTH } from '../types/sidebar';

interface SidebarContextType {
    isCollapsed: boolean;
    sidebarWidth: number;
    toggleSidebar: () => void;
    setCollapsed: (collapsed: boolean) => void;
    setSidebarWidth: (width: number) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const useSidebar = (): SidebarContextType => {
    const context = useContext(SidebarContext);
    if (context === undefined) {
        throw new Error('useSidebar must be used within a SidebarProvider');
    }
    return context;
};

interface SidebarProviderProps {
    children: ReactNode;
}

export const SidebarProvider: React.FC<SidebarProviderProps> = ({ children }) => {
    // Calculate initial width from localStorage
    const getInitialWidth = () => {
        const saved = localStorage.getItem('sidebarWidth');
        return saved ? parseInt(saved, 10) : SIDEBAR_DEFAULT_WIDTH;
    };
    
    const initialWidth = getInitialWidth();
    
    const [sidebarWidth, setSidebarWidthState] = useState(initialWidth);
    const [isCollapsed, setIsCollapsed] = useState(() => {
        // If width equals minimum width, sidebar should be collapsed
        return initialWidth === SIDEBAR_MIN_WIDTH;
    });

    // Save to localStorage when width changes
    useEffect(() => {
        localStorage.setItem('sidebarWidth', sidebarWidth.toString());
    }, [sidebarWidth]);

    const toggleSidebar = () => {
        setIsCollapsed(prev => !prev);
        updateSidebarWidth()
    };

    const setCollapsed = (collapsed: boolean) => {
        setIsCollapsed(collapsed);
        updateSidebarWidth()
    };
    
    const updateSidebarWidth = () => {
        if(!isCollapsed) {
            setSidebarWidthState(SIDEBAR_MIN_WIDTH);
        }
        else {
            setSidebarWidthState(SIDEBAR_DEFAULT_WIDTH);
        }
    };

    const setSidebarWidth = (width: number) => {
        // Clamp width between min and max
        const clampedWidth = Math.min(SIDEBAR_MAX_WIDTH, width)
        setSidebarWidthState(clampedWidth);
    };

    const value: SidebarContextType = {
        isCollapsed,
        sidebarWidth,
        toggleSidebar,
        setCollapsed,
        setSidebarWidth,
    };

    return (
        <SidebarContext.Provider value={value}>
            {children}
        </SidebarContext.Provider>
    );
};

export default SidebarProvider;
